import { ReactNode, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { useParams, useRouteMatch, useHistory } from 'react-router-dom';
import { Card, CardContent, Grid, styled } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { DASHBOARD_ALL_FIRMS, DASHBOARD_MY_FIRM } from 'adminConstants/routes';
import GridContainer from 'components/GridContainer';
import CompanyCategoryPieChart from 'components/DashboardWidgets/CompanyCategoryPieChart';
import EisAllotments from 'components/DashboardWidgets/EisAllotments';
import InvestmentsInflowSummary from 'components/DashboardWidgets/InvestmentsInflowSummary';
import TrancheCountsByStatus from 'components/DashboardWidgets/TrancheCountsByStatus';
import OpenTranchesTable from 'components/DashboardWidgets/OpenTranchesTable';
import { useGetRole } from 'hooks/ui/useGetRole';
import LifetimePerformanceSummary from 'components/DashboardWidgets/LifetimePerformanceSummary';
import EntitySummary from 'components/DashboardWidgets/EntitySummary';
import TopEntitiesTables from 'components/DashboardWidgets/TopEntitiesTables';
import ClosedTrancheDeployments from 'components/DashboardWidgets/ClosedTrancheDeployments';
import { useFirm } from 'hooks/data/firm/useFirm';
import PageContainer from 'components/PageContainer';
import { FundTypeSelectorOptionsContextProvider } from 'components/DashboardWidgets/FundTypeSelector/FundTypeSelectorOptionsContext';

const useStyles = makeStyles()((theme) => ({
  scrollableTable: {
    maxHeight: '400px',
    overflow: 'auto',
    marginBottom: '24px',
    paddingBottom: '0 !important',
  },
  heading: {
    margin: '1rem 0',
  },
  green: {
    color: theme.palette.text.rootColor,
  },
  divider: {
    margin: '0 24px',
  },
  flexGrid: {
    display: 'flex',
    flexDirection: 'column',
    gridGap: '1rem',
    alignItems: 'stretch',
  },
  childDiv100: {
    '&> div': {
      height: '100%',
    },
  },
  flexGrow: {
    flexGrow: 1,
  },
  link: {
    fontSize: '14px',
    color: theme.palette.text.rootColor,
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  linkContainer: {
    flex: '1',
    paddingTop: '10px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
  },
  dcont: {
    display: 'flex',
    flexDirection: 'column',
  },
  dFlex: {
    display: 'flex',
    alignItems: 'center',
  },
}));

const LazyTileContainer = styled('div')(() => ({
  minHeight: '1px',
  width: '100%',
}));

type LazyDashboardTileProps = {
  children: ReactNode;
};

const LazyDashboardTile: React.FC<LazyDashboardTileProps> = ({ children }) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    rootMargin: '200px 0px',
  });

  return (
    <LazyTileContainer ref={ref}>{inView ? children : null}</LazyTileContainer>
  );
};

const FirmDashboard: React.FC = () => {
  const history = useHistory();
  const { classes, cx } = useStyles();
  const { firmId } = useParams<{ firmId: string }>();
  const { firmId: usersFirmId, firmName, isSuperAdmin } = useGetRole();
  const { firms } = useFirm({ params: {} });

  const isMyFirmPage = useRouteMatch(DASHBOARD_MY_FIRM)?.isExact;
  const isAllFirmsDashboard = useRouteMatch(DASHBOARD_ALL_FIRMS)?.isExact;

  const firmIdToQuery = isMyFirmPage ? usersFirmId : firmId;
  const adminUserOnMyFirmsPage = isMyFirmPage && isSuperAdmin;

  const params: { firmId?: string } = {};
  if (!isAllFirmsDashboard && !adminUserOnMyFirmsPage) {
    params.firmId = firmIdToQuery;
  }

  const dashboardFirmName = firmName
    ? firmName
    : firms?.data?.result?.find((firm) => firm._id === firmIdToQuery)?.firmName;

  useEffect(() => {
    if (isAllFirmsDashboard && !isSuperAdmin) {
      history.push(DASHBOARD_MY_FIRM);
    }
  }, [isAllFirmsDashboard, isSuperAdmin]);

  const heading = isAllFirmsDashboard
    ? 'Master dashboard'
    : `${
        dashboardFirmName ? `${dashboardFirmName} master` : 'Master'
      } dashboard`;

  return (
    <PageContainer heading={heading}>
      <GridContainer spacing={5}>
        <FundTypeSelectorOptionsContextProvider firmId={firmId}>
          <Grid size={{ md: 6, xs: 12, xl: 7 }} className={classes.flexGrid}>
            {/* @ts-expect-error */}
            <InvestmentsInflowSummary firmId={firmIdToQuery} />
          </Grid>

          <Grid
            size={{ md: 6, xs: 12, xl: 5 }}
            className={cx(classes.flexGrid, classes.childDiv100)}
          >
            <LifetimePerformanceSummary
              parentClasses={classes}
              firmId={firmIdToQuery}
            />
          </Grid>
        </FundTypeSelectorOptionsContextProvider>

        {/* @ts-expect-error */}
        <EntitySummary parentClasses={classes} firmId={firmIdToQuery} />

        <Grid size={{ md: 8, xs: 12 }} className={classes.flexGrid}>
          <LazyDashboardTile>
            <EisAllotments firmId={firmIdToQuery} />
          </LazyDashboardTile>
          <LazyDashboardTile>
            <TrancheCountsByStatus firmId={firmIdToQuery} />
          </LazyDashboardTile>
        </Grid>

        <Grid size={{ md: 4, xs: 12 }}>
          <LazyDashboardTile>
            <Card className={classes.flexGrow}>
              <CardContent style={{ marginTop: '1rem' }}>
                <CompanyCategoryPieChart
                  title="Fund portfolio by sector"
                  subtitle="(Ordered by current value)"
                  firmId={firmIdToQuery}
                />
              </CardContent>
            </Card>
          </LazyDashboardTile>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <LazyDashboardTile>
            <TopEntitiesTables firmId={firmIdToQuery} />
          </LazyDashboardTile>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <LazyDashboardTile>
            <OpenTranchesTable firmId={firmIdToQuery} classes={classes} />
          </LazyDashboardTile>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <LazyDashboardTile>
            <GridContainer>
              <ClosedTrancheDeployments
                firmId={firmIdToQuery}
                classes={classes}
              />
            </GridContainer>
          </LazyDashboardTile>
        </Grid>
      </GridContainer>
    </PageContainer>
  );
};

export default FirmDashboard;
